import { useDisclosure } from "@msuite/ui/hooks";
import { ColoredIcon, icon } from "@msuite/ui/icons";
import { useTranslation } from "@msuite/ui/localization";
import { Menu } from "@msuite/ui/menu";
import { ConfirmModal, useAsyncModal } from "@msuite/ui/modal";
import { Portal } from "@msuite/ui/portal";
import { invalidate, useQueryClient } from "@msuite/ui/query";
import type { ChildrenProps } from "@msuite/ui/types";
import { responseHandler } from "@msuite/ui/utils";
import { ticketSchema } from "@msuite/utils/schemas";
import type { Enums, Tables } from "@msuite/utils/types";
import React from "react";
import { client } from "@/hono";
import { useTicketRules } from "@/hooks/use-ticket-rules";
import { useTicketTypes } from "@/hooks/use-ticket-types";
import { AddTicketModal } from "@/modals/projects/add-ticket-modal";
import { EditTicketModal } from "@/modals/projects/edit-ticket-modal";
import { TicketInformationModal } from "@/modals/projects/ticket-information-modal";
import { tidyDividers } from "./tidy-dividers";

/** Props Interface */
interface TicketCardContextMenuProps extends ChildrenProps {
	ticket: Tables<"tickets">;
	onCompleteEdit?: () => Promise<void> | void;
}

export function TicketCardContextMenu({
	children,
	ticket,
	onCompleteEdit,
}: TicketCardContextMenuProps) {
	/** Hooks */
	const controller = useAsyncModal<boolean>();
	const { t } = useTranslation();
	const addTicketModal = useDisclosure();
	const editTicketModal = useDisclosure();
	const ticketInformationModal = useDisclosure();
	const { data } = useTicketTypes();
	const qc = useQueryClient();
	const { updateIsDenied, createIsDenied } = useTicketRules();

	/** Computed */
	// The menu carries the ticket it is about, so both rules bind against the row rather than the
	// category: correcting one asks for the site it sits on, and raising or copying one asks for
	// the site the new ticket would land on.
	const cannotUpdate = updateIsDenied(ticket);
	const cannotCreate = createIsDenied(ticket.projectId);

	/** Functions */
	async function handleOnChangeTicketState(state: Enums<"projectState">) {
		const response = await client.tickets[":ticketId"].$patch({
			json: {
				...ticketSchema.parse(ticket),
				uploadResults: [],
				state,
			},
			param: {
				ticketId: ticket.id,
			},
		});
		await responseHandler(response);
		await qc.invalidateQueries({
			predicate: invalidate([ticket.id, ticket.projectId]),
		});
	}

	function handleOnNavigateToProject() {
		window.open(`/projects/${ticket.projectId}`);
	}

	/** Render */
	return (
		<React.Fragment>
			<Menu
				type="context-menu"
				items={() =>
					tidyDividers([
						ticket.number,
						"divider",
						{
							label: t("projects.project_information"),
							icon: icon("Info"),
							onClick: handleOnNavigateToProject,
						},
						{
							label: t("projects.ticket_information"),
							icon: icon("Ticket"),
							onClick: ticketInformationModal.onOpen,
						},
						"divider",
						cannotCreate
							? null
							: {
									label: t("projects.add_ticket"),
									icon: icon("Plus"),
									onClick: addTicketModal.onOpen,
								},
						cannotUpdate
							? null
							: {
									label: t("projects.edit_ticket"),
									icon: icon("Edit"),
									onClick: editTicketModal.onOpen,
								},
						cannotCreate
							? null
							: {
									label: t("projects.copy_ticket"),
									icon: icon("CopyPlus"),
									onClick: () => {},
									items: data?.tableTypes?.map((ticketType) => ({
										label: ticketType.name,
										icon: () => (
											<ColoredIcon
												color={ticketType.color}
												iconName={ticketType.icon}
											/>
										),
										onClick: async () => {
											if (!ticket?.id) return;
											const response = await client.tickets[":ticketId"].copy.$post({
												json: {
													type: ticketType.id,
												},
												param: {
													ticketId: ticket.id,
												},
											});
											await responseHandler(response);
											await qc.invalidateQueries({
												predicate: invalidate([ticket.id, ticket.projectId]),
											});
										},
									})),
								},
						"divider",
						cannotUpdate
							? null
							: {
									label:
										ticket.state === "active"
											? t("projects.deactivate_ticket")
											: t("projects.activate_ticket"),
									icon: ticket.state === "active" ? icon("Trash2") : icon("ChevronsUp"),
									onClick: async () => {
										await handleOnChangeTicketState(
											ticket.state === "active" ? "inactive" : "active",
										);
									},
									isDanger: ticket.state === "active",
								},
						cannotUpdate
							? null
							: {
									label: t("general.extended"),
									icon: undefined,
									onClick: () => {},
									items: [
										{
											label: t("projects.archive_ticket"),
											icon: icon("Archive"),
											onClick: async () => {
												await handleOnChangeTicketState("archived");
											},
											isDanger: true,
										},
									],
								},
					])
				}
				triggerAsChild={false}
			>
				{children}
			</Menu>
			<Portal>
				{ticketInformationModal.isOpen && (
					<TicketInformationModal
						isOpen={ticketInformationModal.isOpen}
						onClose={ticketInformationModal.onClose}
						ticketId={ticket.id}
						projectId={ticket.projectId}
					/>
				)}
				{addTicketModal.isOpen && (
					<AddTicketModal
						isOpen={addTicketModal.isOpen}
						onClose={addTicketModal.onClose}
						projectId={ticket.projectId}
					/>
				)}
				{editTicketModal.isOpen && (
					<EditTicketModal
						isOpen={editTicketModal.isOpen}
						onClose={editTicketModal.onClose}
						projectId={ticket.projectId}
						ticketId={ticket.id}
						onCompleteEdit={onCompleteEdit}
					/>
				)}
				{controller.isOpen && (
					<ConfirmModal
						message="Are you sure you want to delete this ticket?"
						controller={controller}
					/>
				)}
			</Portal>
		</React.Fragment>
	);
}
